import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  MessageSquare, Search, Plus, ThumbsUp, MessageCircle, Eye, 
  Pin, Tag, Clock, TrendingUp, Flame, User, Send, ChevronRight,
  Filter, ArrowUp
} from 'lucide-react';
import { toast } from 'sonner';


// Categories matching backend seeded categories
const CATEGORIES = [
  { id: 'general', name: 'General Discussion', icon: MessageSquare, color: 'text-blue-500' },
  { id: 'cognitive', name: 'Cognitive Enhancement', icon: TrendingUp, color: 'text-purple-500' },
  { id: 'recovery', name: 'Recovery & Healing', icon: MessageCircle, color: 'text-green-500' },
  { id: 'gh', name: 'Growth Hormone', icon: TrendingUp, color: 'text-orange-500' },
  { id: 'protocols', name: 'Protocol Sharing', icon: Tag, color: 'text-cyan-500' },
  { id: 'suppliers', name: 'Supplier Reviews', icon: User, color: 'text-yellow-500' },
  { id: 'longevity', name: 'Longevity & Anti-Aging', icon: Flame, color: 'text-pink-500' },
  { id: 'help', name: 'Help & Support', icon: MessageCircle, color: 'text-gray-500' }
];

export default function Forums() {
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [categoryStats, setCategoryStats] = useState({}); // { cat_general: 3, ... }
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewThreadDialog, setShowNewThreadDialog] = useState(false);
  const [newThread, setNewThread] = useState({ title: '', content: '', category_id: 'cat_general' });
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchThreads();
  }, [selectedCategory, activeTab]);

  useEffect(() => {
    fetchCategoryStats();
  }, []);

  const fetchCategoryStats = async () => {
    try {
      const response = await fetch(`${API}/community/categories`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const stats = {};
        (Array.isArray(data) ? data : []).forEach(c => {
          stats[c.category_id] = c.thread_count || 0;
        });
        setCategoryStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch category stats:', error);
    }
  };

  const fetchThreads = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category_id', `cat_${selectedCategory}`);
      if (activeTab === 'trending') params.append('sort', 'trending');
      if (activeTab === 'newest') params.append('sort', 'newest');
      
      const response = await fetch(`${API}/community/threads?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Backend returns { threads: [...], total, page, pages }
        setThreads(data.threads || data);
      }
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchThread = async (threadId) => {
    try {
      const response = await fetch(`${API}/community/threads/${threadId}`, { credentials: 'include' });
      if (response.ok) {
        setSelectedThread(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch thread:', error);
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/community/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newThread)
      });

      if (response.ok) {
        toast.success('Thread created!');
        setShowNewThreadDialog(false);
        setNewThread({ title: '', content: '', category_id: 'cat_general' });
        fetchThreads();
        fetchCategoryStats();
      } else {
        toast.error('Failed to create thread');
      }
    } catch (error) {
      toast.error('Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() || !selectedThread) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/community/threads/${selectedThread.thread_id}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: replyContent })
      });

      if (response.ok) {
        toast.success('Reply posted!');
        setReplyContent('');
        fetchThread(selectedThread.thread_id);
      } else {
        toast.error('Failed to post reply');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (threadId, replyId = null) => {
    try {
      const endpoint = replyId 
        ? `${API}/community/posts/${replyId}/upvote`
        : `${API}/community/threads/${threadId}/upvote`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        if (selectedThread) {
          fetchThread(selectedThread.thread_id);
        } else {
          fetchThreads();
        }
      }
    } catch (error) {
      console.error('Failed to upvote:', error);
    }
  };

  const filteredThreads = threads.filter(thread =>
    thread.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    thread.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryInfo = (categoryId) => {
    // Backend uses 'cat_general' namespace; CATEGORIES uses bare 'general'
    const bareId = (categoryId || '').replace(/^cat_/, '');
    return CATEGORIES.find(c => c.id === bareId) || CATEGORIES[0];
  };

  if (selectedThread) {
    return (
      <ThreadView 
        thread={selectedThread}
        onBack={() => setSelectedThread(null)}
        onReply={handleReply}
        onUpvote={handleUpvote}
        replyContent={replyContent}
        setReplyContent={setReplyContent}
        submitting={submitting}
        user={user}
      />
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="forums-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <MessageSquare className="h-10 w-10 text-primary" />
            Community Forums
          </h1>
          <p className="text-muted-foreground">Discuss protocols, share experiences, ask questions</p>
        </div>

        <Dialog open={showNewThreadDialog} onOpenChange={setShowNewThreadDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-thread-button">
              <Plus className="h-4 w-4 mr-2" />
              New Thread
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Thread</DialogTitle>
              <DialogDescription>
                Start a discussion with the community
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={newThread.category_id} onValueChange={(v) => setNewThread({...newThread, category_id: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={`cat_${cat.id}`}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={newThread.title}
                  onChange={(e) => setNewThread({...newThread, title: e.target.value})}
                  placeholder="What's your question or topic?"
                />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea
                  value={newThread.content}
                  onChange={(e) => setNewThread({...newThread, content: e.target.value})}
                  placeholder="Share more details..."
                  rows={5}
                />
              </div>
              <Button onClick={handleCreateThread} className="w-full" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Thread'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search discussions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="trending">
            <Flame className="h-4 w-4 mr-1" />
            Trending
          </TabsTrigger>
          <TabsTrigger value="newest">
            <Clock className="h-4 w-4 mr-1" />
            Newest
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Category Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          // Use authoritative count from /community/categories (server-side aggregate)
          const count = categoryStats[`cat_${cat.id}`] || 0;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedCategory === cat.id
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Icon className={`h-5 w-5 mb-2 ${cat.color}`} />
              <p className="font-medium text-sm">{cat.name}</p>
              <p className="text-xs text-muted-foreground">{count} threads</p>
            </button>
          );
        })}
      </div>

      {/* Threads List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading discussions...</p>
        </div>
      ) : filteredThreads.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">No Discussions Yet</h2>
          <p className="text-muted-foreground mb-6">
            Be the first to start a conversation!
          </p>
          <Button onClick={() => setShowNewThreadDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Start a Discussion
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredThreads.map(thread => {
            const cat = getCategoryInfo(thread.category_id);
            const CatIcon = cat.icon;
            return (
              <Card
                key={thread.thread_id}
                className="p-4 glass-card hover:border-primary/50 cursor-pointer transition-all"
                onClick={() => fetchThread(thread.thread_id)}
                data-testid={`thread-${thread.thread_id}`}
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={thread.author_picture} />
                    <AvatarFallback>{thread.author_name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {thread.is_pinned && <Pin className="h-4 w-4 text-primary" />}
                      <span className={`text-xs px-2 py-0.5 rounded ${cat.color} bg-current/10`}>
                        {cat.name}
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg truncate">{thread.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {thread.content}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {thread.author_name || 'Anonymous'}
                      </span>
                      <span className="flex items-center gap-1">
                        <ThumbsUp className="h-3 w-3" />
                        {thread.upvote_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        {thread.reply_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {thread.view_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(thread.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ThreadView({ thread, onBack, onReply, onUpvote, replyContent, setReplyContent, submitting, user }) {
  const cat = CATEGORIES.find(c => c.id === thread.category) || CATEGORIES[0];
  const CatIcon = cat.icon;

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="thread-view">
      <Button variant="ghost" onClick={onBack} className="mb-6">
        ← Back to Forums
      </Button>

      {/* Thread Header */}
      <Card className="p-6 glass-card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs px-2 py-0.5 rounded ${cat.color} bg-current/10`}>
            {cat.name}
          </span>
          {thread.is_pinned && (
            <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary flex items-center gap-1">
              <Pin className="h-3 w-3" /> Pinned
            </span>
          )}
        </div>
        
        <h1 className="text-2xl font-bold mb-4">{thread.title}</h1>
        
        <div className="flex items-center gap-3 mb-4">
          <Avatar>
            <AvatarImage src={thread.author_picture} />
            <AvatarFallback>{thread.author_name?.charAt(0) || 'U'}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{thread.author_name || 'Anonymous'}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(thread.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none mb-6">
          <p className="whitespace-pre-wrap">{thread.content}</p>
        </div>

        <div className="flex items-center gap-4 pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUpvote(thread.thread_id)}
            className={thread.user_upvoted ? 'text-primary' : ''}
          >
            <ArrowUp className="h-4 w-4 mr-1" />
            {thread.upvote_count || 0}
          </Button>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <MessageCircle className="h-4 w-4" />
            {thread.reply_count || 0} replies
          </span>
          <span className="text-sm text-muted-foreground flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {thread.view_count || 0} views
          </span>
        </div>
      </Card>

      {/* Replies (backend returns 'posts'; legacy 'replies' kept as fallback) */}
      {(() => {
        const replies = thread.posts || thread.replies || [];
        return (
          <>
            <h2 className="text-xl font-semibold mb-4" data-testid="thread-replies-heading">
              Replies ({replies.length})
            </h2>

            <div className="space-y-4 mb-6" data-testid="thread-replies-list">
              {replies.map((reply, index) => (
                <Card key={reply.post_id || reply.reply_id || index} className="p-4 glass-card" data-testid={`thread-reply-${reply.post_id || index}`}>
                  <div className="flex items-start gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={reply.author_picture} />
                      <AvatarFallback>{reply.author_name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm">{reply.author_name || 'Anonymous'}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(reply.created_at).toLocaleString()}
                        </p>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        );
      })()}

      {/* Reply Form */}
      <Card className="p-4 glass-card">
        <h3 className="font-semibold mb-3">Post a Reply</h3>
        <Textarea
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          placeholder="Share your thoughts..."
          rows={3}
          className="mb-3"
        />
        <Button onClick={onReply} disabled={submitting || !replyContent.trim()}>
          <Send className="h-4 w-4 mr-2" />
          {submitting ? 'Posting...' : 'Post Reply'}
        </Button>
      </Card>
    </div>
  );
}
